import { useState } from "react";
import { storageGet, storageSet } from "@/shared/utils/storage";

/**
 * Local calendar date as YYYY-MM-DD. Deliberately NOT `toISOString()`, which
 * is UTC and would reset the dismissal at the wrong local midnight (e.g.
 * 4-7 PM for US timezones).
 */
export function todayIso(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** True when the stored dismissal date equals today's local date. */
export function readDayDismissed(key: string): boolean {
  return storageGet<string>(key) === todayIso();
}

/** Persist a dismissal that lasts until the next local calendar day. */
export function persistDayDismissal(key: string): void {
  storageSet(key, todayIso());
}

/**
 * Dismissal state backed by localStorage that resets each calendar day.
 * Stores the dismissal date (YYYY-MM-DD); on read, compares against today —
 * a date from a previous day is treated as not dismissed, so the content
 * reappears automatically the next morning without any cleanup job.
 */
export function useDayDismissal(key: string): readonly [boolean, () => void] {
  const [dismissed, setDismissed] = useState(() => readDayDismissed(key));

  const dismiss = () => {
    persistDayDismissal(key);
    setDismissed(true);
  };

  return [dismissed, dismiss] as const;
}
